import React from "react";
import { RefreshCw } from "lucide-react";

import { useAppUpdate } from "@/hooks/useAppUpdate";
import { useI18nStore } from "@/stores/i18nStore";

/**
 * "Load new app version." - shown whenever a newer version is waiting.
 *
 * Deliberately not dismissible. An installed app can run for days without a
 * reload, and inside the Android wrapper there is no address bar to reload
 * from, so a prompt the user can wave away is a prompt that leaves them on an
 * old build indefinitely - with no way back to this offer. It disappears by
 * being acted on, and by nothing else.
 *
 * Positioned clear of everything else that floats: the offline banner owns the
 * strip under the header, and the install banner the bottom corner, so this
 * sits above the latter on a narrow screen and centred on a wide one. Covering
 * the header - where the app's own navigation lives - would be worse than any
 * of them.
 */
export const UpdateAvailableBanner: React.FC = () => {
  const { updateAvailable, applyUpdate } = useAppUpdate();
  const { t } = useI18nStore();

  if (!updateAvailable) {
    return null;
  }

  return (
    <div
      className="fixed inset-x-0 bottom-24 z-[60] flex justify-center px-4 sm:bottom-6"
      data-testid="update-available-banner"
      role="status"
    >
      <button
        type="button"
        onClick={applyUpdate}
        data-testid="update-available-action"
        className="flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-blue-700"
      >
        <RefreshCw aria-hidden="true" className="h-4 w-4" />
        {t.updateAvailable}
      </button>
    </div>
  );
};

export default UpdateAvailableBanner;
